import type { MeResponse } from '@tvitvi/shared';
import { useNavigate } from '@tanstack/react-router';
import type { ReactNode } from 'react';
import { useTranslation } from 'react-i18next';

import { actorFromMe, allows } from '@/auth/actor';
import { useAuth } from '@/auth/auth-context';
import {
  DropdownMenu,
  DropdownMenuContent,
  DropdownMenuItem,
  DropdownMenuSeparator,
  DropdownMenuTrigger,
} from '@/components/ui/DropdownMenu';
import { ICONS } from '@/components/ui/icons';
import { categoricalColorVar, initials } from '@/lib/format';

export function UserMenu({ me }: { me: MeResponse }): ReactNode {
  const { t } = useTranslation();
  const navigate = useNavigate();
  const { signOut } = useAuth();
  const Profile = ICONS.profile;
  const Appearance = ICONS.light;
  const Bell = ICONS.bell;
  const SignOut = ICONS.signOut;
  const Exports = ICONS.download;
  /* «Mine eksporter» (docs/EKSPORT.md §8): bare for den som kan eksportere. */
  const hasExports = allows(actorFromMe(me), 'export:create');

  return (
    <DropdownMenu>
      <DropdownMenuTrigger
        /* Toppradens brukermeny er navigasjon, ikke en handling. Se knappevakten. */
        data-unstyled
        data-testid="user-menu"
        aria-label={t('common.actions.openMenu')}
        className="flex items-center gap-2 rounded-sm px-1.5 py-1 ring-focus hover:bg-surface-sunken"
      >
        <span
          aria-hidden
          className="flex size-5 items-center justify-center rounded-full text-2xs font-semibold text-inverse"
          style={{ backgroundColor: categoricalColorVar(me.person.id) }}
        >
          {initials(me.person.displayName)}
        </span>
        <span className="hidden max-w-40 truncate text-xs text-default sm:block">
          {me.person.displayName}
        </span>
      </DropdownMenuTrigger>

      <DropdownMenuContent align="end">
        <div className="px-2 py-1.5">
          <p className="truncate text-xs font-medium text-default">{me.person.displayName}</p>
          <p className="truncate text-2xs text-subtle">{me.user.email}</p>
        </div>
        <DropdownMenuSeparator />
        {/*
          Your account, and only your account. This used to say "Innstillinger" and lead to a menu
          that also held the organisation's permission matrix — two different kinds of thing behind
          one word. The organisation moved to the sidebar on 28 August; what is left here is the
          name, the language and the password, which is what a person expects to find behind their
          own face in the corner.
        */}
        <DropdownMenuItem onSelect={() => void navigate({ to: '/profile' })}>
          <Profile aria-hidden className="size-3.5" />
          <span>{t('nav.profile')}</span>
        </DropdownMenuItem>
        {/* Utseende (Glass 2): som profilen, noe som gjelder deg og ikke huset. */}
        <DropdownMenuItem
          data-testid="user-menu-appearance"
          onSelect={() => void navigate({ to: '/profile/appearance' })}
        >
          <Appearance aria-hidden className="size-3.5" />
          <span>{t('appearance.title')}</span>
        </DropdownMenuItem>
        {/* Varslingsvalgene (F51): som utseendet, noe som gjelder deg og ikke huset. */}
        <DropdownMenuItem
          data-testid="user-menu-notifications"
          onSelect={() => void navigate({ to: '/profile/notifications' })}
        >
          <Bell aria-hidden className="size-3.5" />
          <span>{t('notificationPreferences.title')}</span>
        </DropdownMenuItem>
        {/* «Mine vakter» står i den personlige gruppen i sidemenyen (QA13 103, 253.77), ikke her. */}
        {hasExports ? (
          <DropdownMenuItem
            data-testid="user-menu-exports"
            onSelect={() => void navigate({ to: '/exports' })}
          >
            <Exports aria-hidden className="size-3.5" />
            <span>{t('export.ui.page.title')}</span>
          </DropdownMenuItem>
        ) : null}
        {/* Control Room står nederst i sidemenyen for en plattformadmin (sak 238), ikke her. */}
        <DropdownMenuItem
          onSelect={() => {
            void signOut().then(() =>
              navigate({ to: '/sign-in', search: { redirect: undefined } }),
            );
          }}
        >
          <SignOut aria-hidden className="size-3.5" />
          <span>{t('common.actions.signOut')}</span>
        </DropdownMenuItem>
      </DropdownMenuContent>
    </DropdownMenu>
  );
}
